/**
 * DocumentComparisonPanel - Orchestrator for the document comparison feature.
 *
 * Manages the full lifecycle: idle → uploading → processing → completed/failed.
 * Uploads the zip directly as multipart/form-data to the backend, which stores
 * it via Django FileField and kicks off the Temporal comparison workflow.
 */

import {
  ArrowPathIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { ChevronDown, ChevronRight, Clock } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import type { DocumentComparisonSchema } from '@/services/api/documentComparisonService';
import {
  useCreateDocumentComparisonMutation,
  useGetDocumentComparisonQuery,
  useListDocumentComparisonsQuery,
} from '@/services/api/documentComparisonService';

import { DocumentComparisonResults } from './DocumentComparisonResults';
import { DocumentComparisonStatusView } from './DocumentComparisonStatus';
import { DocumentComparisonUploadZone } from './DocumentComparisonUploadZone';

type Phase = 'idle' | 'uploading' | 'processing' | 'completed' | 'failed';

interface DocumentComparisonPanelProps {
  matterId: string;
  documentEntityChangeIds: string[];
}

/* ── Panel component ───────────────────────────────────────────────────── */

export const DocumentComparisonPanel: React.FC<
  DocumentComparisonPanelProps
> = ({ matterId, documentEntityChangeIds }) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [activeComparisonId, setActiveComparisonId] = useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  // Ref to track the latest comparison data for the effect below
  const comparisonDataRef = useRef<DocumentComparisonSchema | undefined>(
    undefined,
  );

  // RTK Query hooks
  const [createComparison] = useCreateDocumentComparisonMutation();

  // Poll for active comparison status
  const { data: comparisonData } = useGetDocumentComparisonQuery(
    { matterId, comparisonId: activeComparisonId ?? '' },
    {
      skip: !activeComparisonId || phase !== 'processing',
      pollingInterval: 2000,
    },
  );

  // List previous comparisons
  const { data: previousComparisons } = useListDocumentComparisonsQuery(
    { matterId },
    { skip: !matterId },
  );

  // Sync polling data to phase transitions
  useEffect(() => {
    if (!comparisonData || phase !== 'processing') return;
    comparisonDataRef.current = comparisonData;

    if (comparisonData.status === 'completed') {
      setPhase('completed');
    } else if (comparisonData.status === 'failed') {
      setErrorMessage(
        comparisonData.error_message ?? 'Comparison failed unexpectedly.',
      );
      setPhase('failed');
    }
  }, [comparisonData, phase]);

  // Handle file selection → upload as multipart/form-data → create comparison
  const handleFileSelected = useCallback(
    async (file: File) => {
      setPhase('uploading');
      setErrorMessage(null);

      try {
        // Upload the zip directly to the backend via multipart form data.
        // Django stores it via FileField to S3 and kicks off the Temporal workflow.
        const comparison = await createComparison({
          matterId,
          file,
        }).unwrap();

        setActiveComparisonId(comparison.id);
        setPhase('processing');
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Upload failed. Please try again.';
        setErrorMessage(message);
        setPhase('failed');
      }
    },
    [matterId, createComparison],
  );

  const handleRetry = useCallback(() => {
    setPhase('idle');
    setActiveComparisonId(null);
    setErrorMessage(null);
  }, []);

  const handleViewPastComparison = useCallback(
    (comparison: DocumentComparisonSchema) => {
      setActiveComparisonId(comparison.id);
      comparisonDataRef.current = comparison;
      if (comparison.status === 'completed') {
        setPhase('completed');
      } else if (comparison.status === 'failed') {
        setErrorMessage(comparison.error_message ?? 'Comparison failed.');
        setPhase('failed');
      } else {
        setPhase('processing');
      }
    },
    [],
  );

  // Determine which comparison data to show in completed state
  const displayComparison = comparisonDataRef.current ?? comparisonData;

  // Filter history: exclude the active one and sort by most recent
  const historyItems = (previousComparisons ?? [])
    .filter((c) => c.id !== activeComparisonId)
    .slice(0, 5);

  return (
    <div className="sticky top-8">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {/* Gradient left accent */}
        <div className="relative">
          <div
            className="absolute inset-y-0 left-0 w-1"
            style={{
              background: 'linear-gradient(to bottom, #6366f1, #8b5cf6, #a78bfa)',
            }}
          />

          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
            <DocumentMagnifyingGlassIcon className="h-4.5 w-4.5 text-indigo-500" />
            <h4 className="text-sm font-semibold text-gray-800">
              Document Comparison
            </h4>
          </div>

          {/* Content area */}
          <div className="px-4 py-3">
            {/* IDLE */}
            {phase === 'idle' && (
              <DocumentComparisonUploadZone
                onFileSelected={handleFileSelected}
              />
            )}

            {/* UPLOADING */}
            {phase === 'uploading' && (
              <div className="flex items-center gap-2 text-sm text-gray-600 py-4">
                <ArrowPathIcon className="h-4 w-4 animate-spin text-indigo-500" />
                Uploading zip & starting comparison...
              </div>
            )}

            {/* PROCESSING */}
            {phase === 'processing' && comparisonData && (
              <DocumentComparisonStatusView
                status={comparisonData.status}
                processedCount={comparisonData.processed_count}
                totalExternalFiles={comparisonData.total_external_files}
                totalInternalFiles={comparisonData.total_internal_files}
              />
            )}
            {phase === 'processing' && !comparisonData && (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                Starting comparison...
              </div>
            )}

            {/* COMPLETED */}
            {phase === 'completed' && displayComparison && (
              <div className="space-y-3">
                <DocumentComparisonResults
                  comparison={displayComparison}
                  matterId={matterId}
                  documentEntityChangeIds={documentEntityChangeIds}
                />
                <button
                  type="button"
                  onClick={handleRetry}
                  className="w-full text-xs text-gray-400 hover:text-gray-600 transition-colors pt-1"
                >
                  Run new comparison
                </button>
              </div>
            )}

            {/* FAILED */}
            {phase === 'failed' && (
              <div className="space-y-3">
                <div className="rounded-md bg-red-50 px-3 py-2.5 text-xs text-red-700 flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{errorMessage ?? 'Something went wrong.'}</span>
                </div>
                <button
                  type="button"
                  onClick={handleRetry}
                  className="w-full rounded-md bg-gray-100 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200"
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Previous comparisons */}
        {historyItems.length > 0 && (
          <div className="border-t border-gray-100">
            <button
              type="button"
              onClick={() => setShowHistory(!showHistory)}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-xs text-gray-500 hover:bg-gray-50/50 transition-colors"
            >
              {showHistory ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              <Clock className="h-3.5 w-3.5" />
              Previous comparisons ({historyItems.length})
            </button>
            {showHistory && (
              <div className="px-4 pb-3 space-y-1">
                {historyItems.map((comp) => (
                  <button
                    key={comp.id}
                    type="button"
                    onClick={() => handleViewPastComparison(comp)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-xs transition-colors',
                      'hover:bg-gray-50 text-gray-600',
                    )}
                  >
                    <span className="truncate">
                      {new Date(comp.created_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    <span
                      className={cn(
                        'text-[10px] font-medium uppercase tracking-wide',
                        comp.status === 'completed' && 'text-green-600',
                        comp.status === 'failed' && 'text-red-500',
                        comp.status !== 'completed' &&
                          comp.status !== 'failed' &&
                          'text-amber-500',
                      )}
                    >
                      {comp.status.replace('_', ' ')}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
